import { useState, useRef, useEffect } from "react";
import { Eye, EyeOff, Ban, Check } from "lucide-react";

const inputCls = "w-full rounded-xl border border-brand-purple/25 bg-white px-3 text-base text-brand-purple placeholder:text-brand-purple/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta focus-visible:ring-offset-2 aria-[invalid=true]:border-red-700";

function Help({ id, hint, error }) {
  return (<>
    {hint && <span id={`${id}-hint`} className="mt-1 block text-sm text-brand-purple/75">{hint}</span>}
    {error && <span id={`${id}-error`} role="alert" className="mt-1 block text-sm font-medium text-red-800" data-testid={`${id}-error`}>{error}</span>}
  </>);
}

const describedBy = (id, hint, error) => [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined;

export function TextField({ id, label, hint, error, optional, className = "", ...props }) {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1 block font-medium text-brand-purple">{label}{optional && <span className="font-normal text-brand-purple/70"> (freiwillig)</span>}</span>
      <input id={id} data-testid={id} aria-invalid={error ? "true" : undefined} aria-describedby={describedBy(id, hint, error)}
        className={`${inputCls} h-12 ${className}`} {...props} />
      <Help id={id} hint={hint} error={error} />
    </label>
  );
}

export function TextArea({ id, label, hint, error, optional, max = 300, value, rows = 3, ...props }) {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1 flex items-baseline justify-between gap-2 font-medium text-brand-purple">
        <span>{label}{optional && <span className="font-normal text-brand-purple/70"> (freiwillig)</span>}</span>
        <span className="text-xs font-normal text-brand-purple/70" data-testid={`${id}-count`}>{Array.from(value || "").length}/{max}</span>
      </span>
      <textarea id={id} data-testid={id} value={value} rows={rows} maxLength={max} aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy(id, hint, error)} className={`${inputCls} py-2`} {...props} />
      <Help id={id} hint={hint} error={error} />
    </label>
  );
}

export function PasswordField({ id, label, value, onChange, error, hint, autoComplete = "new-password" }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label htmlFor={id} className="mb-1 block font-medium text-brand-purple">{label}</label>
      <div className="relative">
        <input id={id} data-testid={id} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete}
          aria-invalid={error ? "true" : undefined} aria-describedby={describedBy(id, hint, error)} className={`${inputCls} h-12 pr-14`} />
        <button type="button" onClick={() => setShow((s) => !s)} aria-pressed={show} aria-label={show ? "Passwort verbergen" : "Passwort anzeigen"}
          data-testid={`${id}-toggle`} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-brand-purple hover:bg-brand-purple/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta">
          {show ? <EyeOff className="h-5 w-5" aria-hidden="true" /> : <Eye className="h-5 w-5" aria-hidden="true" />}
        </button>
      </div>
      <Help id={id} hint={hint} error={error} />
    </div>
  );
}

export function CheckList({ legend, hint, options, value, onChange, testid, error }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <fieldset data-testid={testid}>
      <legend className="mb-1 font-medium text-brand-purple">{legend}</legend>
      {hint && <p className="mb-2 text-sm text-brand-purple/75">{hint}</p>}
      <div className="space-y-2">
        {options.map(([k, label, note]) => (
          <label key={k} className={`flex min-h-[48px] cursor-pointer items-start gap-3 rounded-xl border-2 px-3 py-2.5 ${value.includes(k) ? "border-brand-magenta bg-brand-magenta/5" : "border-brand-purple/15 bg-white"}`}>
            <input type="checkbox" checked={value.includes(k)} onChange={() => toggle(k)} data-testid={`${testid}-${k}`}
              className="mt-0.5 h-5 w-5 shrink-0 accent-[#D50B58]" />
            <span><span className="block font-medium text-brand-purple">{label}</span>{note && <span className="block text-sm text-brand-purple/75">{note}</span>}</span>
          </label>
        ))}
      </div>
      {error && <p role="alert" className="mt-1 text-sm font-medium text-red-800" data-testid={`${testid}-error`}>{error}</p>}
    </fieldset>
  );
}

export function RadioList({ legend, name, options, value, onChange, testid, hint }) {
  return (
    <fieldset data-testid={testid}>
      <legend className="mb-1 font-medium text-brand-purple">{legend}</legend>
      {hint && <p className="mb-2 text-sm text-brand-purple/75">{hint}</p>}
      <div className="space-y-2">
        {options.map(([k, label, note]) => (
          <label key={k} className={`flex min-h-[48px] cursor-pointer items-start gap-3 rounded-xl border-2 px-3 py-2.5 ${value === k ? "border-brand-magenta bg-brand-magenta/5" : "border-brand-purple/15 bg-white"}`}>
            <input type="radio" name={name} checked={value === k} onChange={() => onChange(k)} data-testid={`${testid}-${k}`}
              className="mt-0.5 h-5 w-5 shrink-0 accent-[#D50B58]" />
            <span><span className="block font-medium text-brand-purple">{label}</span>{note && <span className="block text-sm text-brand-purple/75">{note}</span>}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

// Final step without a Core endpoint: shown after the member triggers it; nothing is stored or sent.
export function BlockedNotice({ what = "gespeichert", children, testid = "blocked-notice" }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.focus(); }, []);
  return (
    <div ref={ref} tabIndex={-1} role="alert" data-testid={testid}
      className="flex gap-3 rounded-[12px] border border-v3-line bg-v3-sand p-4 text-v3-ink outline-none focus-visible:ring-2 focus-visible:ring-v3-magenta">
      <Ban className="mt-0.5 h-5 w-5 shrink-0 text-v3-amber" aria-hidden="true" />
      <div className="space-y-1 text-[14px]">
        <p className="font-bold">Noch nicht verfügbar – nichts wurde {what}.<span className="sr-only"> </span></p>
        <p>{children || "Diese Funktion wird gerade mit dem gemeinsamen VISIBILIS-System eingerichtet."} Deine Eingaben bleiben nur auf dieser Seite, bis du sie verlässt.</p>
      </div>
    </div>
  );
}

export function Steps({ steps, current, testid = "form-steps" }) {
  return (
    <ol className="mb-5 flex gap-2" aria-label={`Schritt ${current + 1} von ${steps.length}: ${steps[current]}`} data-testid={testid}>
      {steps.map((s, i) => (
        <li key={s} aria-current={i === current ? "step" : undefined} className="min-w-0 flex-1">
          <span className={`block h-1.5 rounded-full ${i <= current ? "bg-brand-magenta" : "bg-brand-purple/15"}`} aria-hidden="true" />
          <span className={`mt-1 flex items-center gap-1 text-xs ${i === current ? "font-semibold text-brand-purple" : "text-brand-purple/70"}`}>
            {i < current && <Check className="h-3 w-3" aria-hidden="true" />}<span className="break-words">{s}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

export const FormCard = ({ children, testid }) => (
  <div className="space-y-5 rounded-2xl border border-brand-purple/10 bg-white p-5" data-testid={testid}>{children}</div>
);

export const Actions = ({ children }) => <div className="flex flex-wrap gap-2 border-t border-brand-purple/10 pt-4">{children}</div>;
